import type { ErrorEvent } from '@sentry/react';

/**
 * Whether `value` is an Axios "Network Error": the browser got no usable response at all
 * (connection dropped, Cloudflare in the way, laptop waking up). The request usually never
 * reached the backend, so there is nothing to fix in our code.
 *
 * Matches both a live `AxiosError` and the plain-object copy Redux Toolkit makes when a
 * thunk rejects with one (`.unwrap()` hands callers `{ name, message, stack, code }`).
 */
function isAxiosNetworkError(value: unknown): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const { name, code } = value as { name?: unknown; code?: unknown };
    return name === 'AxiosError' && code === 'ERR_NETWORK';
}

/**
 * Drops the issues `captureConsoleIntegration` raises when a page `console.error`s an Axios
 * network error. The user already sees the page's own error message, and a backend that is
 * really down shows up in the backend's monitoring, not as one `[object Object]` issue per
 * open tab (ADMI-FRONTEND-69).
 */
export function dropConsoleLoggedNetworkErrors(event: ErrorEvent): ErrorEvent | null {
    if (event.logger !== 'console') {
        return event;
    }
    const args = event.extra?.arguments;
    if (Array.isArray(args) && args.some(isAxiosNetworkError)) {
        return null;
    }
    return event;
}
